"use client";

/**
 * Top banner site-wide affichée quand l'utilisateur a un RDV en attente
 * (status PENDING) — lit localStorage 'sahha:pending-rdv' écrit par
 * BookingPageClient après réservation.
 *
 * Auto-vérifie toutes les 30s que le RDV est bien encore PENDING (sinon
 * supprime le storage et masque la bannière). 2 actions :
 *   - ANNULER CE RDV → POST /api/appointments/by-code?id=...&code=... { action:'cancel' }
 *   - CONFIRMER CE RDV → ouvre /rdv/[slug]/recap?id=...&code=... pour
 *     compléter (placeholder : actuellement la confirmation est gérée
 *     côté médecin, le patient ne fait que valider la réservation).
 */
import { useEffect, useState } from "react";
import { Calendar, Clock, X } from "@phosphor-icons/react/dist/ssr";
import { safeLocalStorage } from "@/lib/storage/safe-storage";

type PendingRdv = {
  id: string;
  code: string;
  scheduledAt: string;
  physicianSlug: string;
  physicianName: string;
  physicianTitle: string;
  physicianSpecialty?: string;
  physicianPhotoUrl?: string | null;
  motifLabel?: string;
  createdAt: number;
};

const STORAGE_KEY = "sahha:pending-rdv";
const POLL_INTERVAL_MS = 30_000;

// Messages affichés quand l'annulation échoue côté serveur — cf.
// /api/appointments/by-code POST (codes error retournés par la route).
function cancelErrorMessage(data: { error?: string; status?: string } | null | undefined): string {
  switch (data?.error) {
    case "already_finalized":
      return data.status === "COMPLETED" || data.status === "NO_SHOW"
        ? "Ce RDV a déjà eu lieu, il ne peut plus être annulé."
        : "Ce RDV a déjà été annulé.";
    case "not_found":
      return "Ce RDV est introuvable, il a peut-être déjà été supprimé.";
    case "code":
      return "Lien d'annulation invalide ou expiré.";
    case "rate_limited":
      return "Trop de tentatives. Réessayez dans une minute.";
    default:
      return "Impossible d'annuler ce RDV pour le moment. Réessayez.";
  }
}

export function PendingAppointmentBanner() {
  const [pending, setPending] = useState<PendingRdv | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  // Init depuis localStorage + listen events cross-tab
  useEffect(() => {
    function load() {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) { setPending(null); return; }
        const parsed = JSON.parse(raw) as PendingRdv;
        if (!parsed?.id || !parsed?.code) { setPending(null); return; }
        // Auto-purge si le RDV est dans le passé
        if (new Date(parsed.scheduledAt).getTime() < Date.now()) {
          localStorage.removeItem(STORAGE_KEY);
          setPending(null);
          return;
        }
        setPending(parsed);
      } catch {
        setPending(null);
      }
    }
    load();
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) load();
    };
    window.addEventListener("storage", onStorage);
    // Poll DB toutes les 30s pour synchroniser le status
    const poll = setInterval(async () => {
      const raw = safeLocalStorage.getItem(STORAGE_KEY);
      // Guard JSON.parse : si la valeur est literalement "undefined" (string)
      // suite à un setItem(undefined) accidentel, JSON.parse throw
      // SyntaxError "undefined is not valid JSON" (SAHHA-19, 4 events 2026-06-10).
      if (!raw || raw === "undefined" || raw === "null") return;
      let parsed: PendingRdv;
      try {
        parsed = JSON.parse(raw) as PendingRdv;
      } catch {
        // localStorage corrompu — purge pour éviter loop infini
        localStorage.removeItem(STORAGE_KEY);
        return;
      }
      if (!parsed?.id || !parsed?.code) return;
      try {
        const res = await fetch(`/api/appointments/by-code?id=${encodeURIComponent(parsed.id)}&code=${encodeURIComponent(parsed.code)}`);
        const data = await res.json();
        if (!data?.success) return;
        if (data.appointment.status === "CONFIRMED" || data.appointment.status === "CANCELLED" || data.appointment.status === "COMPLETED") {
          localStorage.removeItem(STORAGE_KEY);
          setPending(null);
        }
      } catch {/* noop */}
    }, POLL_INTERVAL_MS);
    return () => {
      window.removeEventListener("storage", onStorage);
      clearInterval(poll);
    };
  }, []);

  async function cancel() {
    if (!pending) return;
    setCancelling(true);
    setCancelError(null);
    try {
      const res = await fetch(`/api/appointments/by-code?id=${encodeURIComponent(pending.id)}&code=${encodeURIComponent(pending.code)}`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ action: "cancel" }),
      });
      const data = await res.json().catch(() => null);
      if (data?.success) {
        localStorage.removeItem(STORAGE_KEY);
        setPending(null);
      } else {
        setCancelError(cancelErrorMessage(data));
      }
    } catch {
      setCancelError("Erreur réseau, vérifiez votre connexion et réessayez.");
    } finally {
      setCancelling(false);
    }
  }

  function dismiss() {
    setHidden(true);
  }

  if (!pending || hidden) return null;

  const date = new Date(pending.scheduledAt);
  const dayLabel = date.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
  const timeLabel = date.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }).replace(":", "h");

  return (
    <div className="border-b border-rose-200 bg-rose-50">
      <div className="mx-auto flex max-w-7xl flex-col items-stretch gap-4 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6 sm:px-6 lg:px-8">
        {/* Left : message + actions */}
        <div className="flex-1">
          <p className="text-[15px] font-bold text-[var(--sahha-text-900)]">
            Attention, votre RDV n&apos;est pas encore confirmé !
          </p>
          <p className="mt-1 text-[13px] text-[var(--sahha-text-700)]">
            Le médecin doit valider votre demande. Vous serez notifié·e par SMS et email dès la confirmation.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={cancel}
              disabled={cancelling}
              className="inline-flex h-10 items-center rounded-full border border-[var(--sahha-primary-600)] bg-white px-5 text-[12px] font-bold uppercase tracking-wider text-[var(--sahha-primary-700)] hover:bg-[var(--sahha-primary-50)] disabled:opacity-60"
            >
              {cancelling ? "Annulation…" : "Annuler ce RDV"}
            </button>
            <a
              href={`/mon-compte/rendez-vous#${pending.id}`}
              className="inline-flex h-10 items-center rounded-full bg-[var(--sahha-primary-600)] px-5 text-[12px] font-bold uppercase tracking-wider text-white hover:bg-[var(--sahha-primary-700)]"
            >
              Voir le détail du RDV
            </a>
          </div>
          {cancelError && (
            <p role="alert" className="mt-2 text-[12.5px] font-medium text-rose-700">
              {cancelError}
            </p>
          )}
        </div>

        {/* Right : recap card */}
        <div className="overflow-hidden rounded-2xl border border-[var(--sahha-border-default)] bg-white shadow-sm sm:max-w-xs">
          <div className="bg-[var(--sahha-primary-900)] px-4 py-2 text-white">
            <div className="flex items-center gap-3 text-[13px]">
              <span className="flex items-center gap-1.5"><Calendar size={14} weight="fill" /> {dayLabel}</span>
              <span className="flex items-center gap-1.5"><Clock size={14} weight="fill" /> {timeLabel}</span>
            </div>
          </div>
          <div className="flex items-center gap-3 px-4 py-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[var(--sahha-primary-100)] text-sm font-bold text-[var(--sahha-primary-700)]">
              {pending.physicianPhotoUrl ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img src={pending.physicianPhotoUrl} alt={pending.physicianName} className="h-full w-full object-cover" />
              ) : (
                (pending.physicianName.split(" ").map((p) => p[0] ?? "").join("").slice(0, 2) || "—").toUpperCase()
              )}
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13px] font-bold text-[var(--sahha-text-900)]">{pending.physicianTitle} {pending.physicianName}</p>
              {pending.physicianSpecialty && <p className="truncate text-[11.5px] text-[var(--sahha-text-600)]">{pending.physicianSpecialty}</p>}
            </div>
          </div>
        </div>

        {/* Dismiss */}
        <button
          type="button"
          onClick={dismiss}
          aria-label="Masquer"
          className="absolute right-3 top-3 hidden h-7 w-7 items-center justify-center rounded-full text-[var(--sahha-text-500)] hover:bg-white/50 sm:flex"
        >
          <X size={14} weight="bold" />
        </button>
      </div>
    </div>
  );
}
